import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
});

axiosInstance.interceptors.request.use(
  config => {
    // Try to get user ID from localStorage
    let userId = null;
    
    try {
      const storedUser = localStorage.getItem('pastelyUser');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        userId = parsedUser.userId || parsedUser.id || parsedUser.sub;
      }
    } catch (error) {
      console.error('Error parsing stored user for API request:', error);
    }
    
    // If we have a userId, add it to headers
    if (userId) {
      config.headers['X-User-ID'] = userId;
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      console.error('Error response:', error.response.data);
      
      // Add specific error handling based on status codes
      if (error.response.status === 404) {
        error.userMessage = 'The requested resource was not found';
      } else if (error.response.status === 500) {
        error.userMessage = 'The server encountered an error. Please try again later';
      } else if (error.response.status === 401) {
        error.userMessage = 'Authentication required. Please log in again';
      } else if (error.response.status === 403) {
        error.userMessage = 'You do not have permission to access this resource';
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
      error.userMessage = 'No response from server. Please check your connection';
    } else {
      // Something else happened while setting up the request
      error.userMessage = 'An error occurred while making the request';
    }
    return Promise.reject(error);
  }
);

const api = {
  // Authentication API calls
  auth: {
    googleLogin: async (googleToken) => {
      try {
        const response = await axiosInstance.post('/api/auth/google', { googleToken });
        return response.data;
      } catch (error) {
        console.error('Error with Google login:', error);
        throw error;
      }
    }
  },

  // User management API calls
  users: {
    getDashboard: async (userId) => {
      if (!userId) {
        console.error('getDashboard called without userId');
        return Promise.reject(new Error('User ID is required'));
      }
      try {
        const response = await axiosInstance.get(`/api/users/${userId}/dashboard`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching dashboard for user ${userId}:`, error);
        throw error;
      }
    },

    getProfile: async (userId) => {
      if (!userId) {
        console.error('getProfile called without userId');
        return Promise.reject(new Error('User ID is required'));
      }
      try {
        const response = await axiosInstance.get(`/api/users/${userId}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching profile for user ${userId}:`, error);
        throw error;
      }
    },

    getUsage: async (userId) => {
      if (!userId) {
        console.error('getUsage called without userId');
        return Promise.reject(new Error('User ID is required'));
      }
      try {
        const response = await axiosInstance.get(`/api/users/${userId}/usage`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching usage for user ${userId}:`, error);
        throw error;
      }
    },

    checkUsageLimits: async (userId, tokens = null, storage = null) => {
      if (!userId) {
        console.error('checkUsageLimits called without userId');
        return Promise.reject(new Error('User ID is required'));
      }
      try {
        const params = new URLSearchParams();
        if (tokens) params.append('tokens', tokens);
        if (storage) params.append('storage', storage);
        
        const response = await axiosInstance.get(`/api/users/${userId}/can-use?${params}`);
        return response.data;
      } catch (error) {
        console.error(`Error checking usage limits for user ${userId}:`, error);
        throw error;
      }
    },

    updateSubscription: async (userId, subscriptionTier) => {
      if (!userId) {
        console.error('updateSubscription called without userId');
        return Promise.reject(new Error('User ID is required'));
      }
      try {
        const response = await axiosInstance.put(`/api/users/${userId}/subscription`, {
          subscriptionTier
        });
        return response.data;
      } catch (error) {
        console.error(`Error updating subscription for user ${userId}:`, error);
        throw error;
      }
    }
  },
  
  // Snippet related API calls
  snippets: {
    getAll: async () => {
      try {
        const response = await axiosInstance.get('/api/snips');
        return response.data;
      } catch (error) {
        console.error('Error fetching snippets:', error);
        return [];
      }
    },
    
    getById: async (id) => {
      if (!id) {
        console.error('getById called without snippet ID');
        return Promise.reject(new Error('Snippet ID is required'));
      }
      try {
        const response = await axiosInstance.get(`/api/snips/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching snippet ${id}:`, error);
        
        // Enhanced error handling for privacy implementation
        if (error.response?.status === 403) {
          const enhancedError = new Error('Access denied: This snippet is private and you do not have permission to view it');
          enhancedError.code = 'SNIPPET_ACCESS_DENIED';
          enhancedError.status = 403;
          enhancedError.snippetId = id;
          throw enhancedError;
        } else if (error.response?.status === 404) {
          const enhancedError = new Error('Snippet not found or may have expired');
          enhancedError.code = 'SNIPPET_NOT_FOUND';
          enhancedError.status = 404;
          enhancedError.snippetId = id;
          throw enhancedError;
        }
        
        throw error;
      }
    },
    
    create: async (snippetData) => {
      try {
        const response = await axiosInstance.post('/api/snips', snippetData);
        return response.data;
      } catch (error) {
        console.error('Error creating snippet:', error);
        if (error.response) {
          console.error('Error response status:', error.response.status);
          console.error('Error response data:', error.response.data);
        }
        throw error;
      }
    },
    
    update: async (id, snippetData) => {
      if (!id) {
        console.error('update called without snippet ID');
        return Promise.reject(new Error('Snippet ID is required'));
      }
      try {
        const response = await axiosInstance.put(`/api/snips/${id}`, snippetData);
        return response.data;
      } catch (error) {
        console.error(`Error updating snippet ${id}:`, error);
        throw error;
      }
    },
    
    delete: async (id) => {
      if (!id) {
        console.error('delete called without snippet ID');
        return Promise.reject(new Error('Snippet ID is required'));
      }
      try {
        await axiosInstance.delete(`/api/snips/${id}`);
        return true;
      } catch (error) {
        console.error(`Error deleting snippet ${id}:`, error);
        throw error;
      }
    },
    
    getRaw: async (id) => {
      if (!id) {
        console.error('getRaw called without snippet ID');
        return Promise.reject(new Error('Snippet ID is required'));
      }
      try {
        const response = await axiosInstance.get(`/api/snips/raw/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching raw snippet ${id}:`, error);
        
        // Enhanced error handling for privacy implementation
        if (error.response?.status === 403) {
          const enhancedError = new Error('Access denied: This snippet is private and you do not have permission to view it');
          enhancedError.code = 'SNIPPET_ACCESS_DENIED';
          enhancedError.status = 403;
          enhancedError.snippetId = id;
          throw enhancedError;
        } else if (error.response?.status === 404) {
          const enhancedError = new Error('Snippet not found or may have expired');
          enhancedError.code = 'SNIPPET_NOT_FOUND';
          enhancedError.status = 404;
          enhancedError.snippetId = id;
          throw enhancedError;
        }
        
        throw error;
      }
    },
    
    getUserSnippets: async (userId) => {
      if (!userId) {
        console.warn('getUserSnippets called without userId');
        return [];
      }
      try {
        const response = await axiosInstance.get('/api/snips/user', {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching user snippets for ${userId}:`, error);
        return [];
      }
    },

    getFolderSnippets: async (folderId, userId) => {
      if (!folderId || !userId) {
        console.warn('getFolderSnippets called without required parameters');
        return [];
      }
      try {
        const response = await axiosInstance.get(`/api/snips/folder/${folderId}`, {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching folder snippets for folder ${folderId}:`, error);
        return [];
      }
    },

    moveSnippetToFolder: async (snippetId, folderId, userId) => {
      if (!snippetId || !userId) {
        console.warn('moveSnippetToFolder called without required parameters');
        return false;
      }
      try {
        const params = new URLSearchParams();
        if (folderId) {
          params.append('folderId', folderId);
        }
        
        await axiosInstance.post(`/api/snips/${snippetId}/move?${params}`, {}, {
          headers: {
            'X-User-ID': userId
          }
        });
        return true;
      } catch (error) {
        console.error(`Error moving snippet ${snippetId} to folder ${folderId}:`, error);
        return false;
      }
    }
  },
  
  // AI Models API calls
  models: {
    getAll: async () => {
      try {
        const response = await axiosInstance.get('/api/models');
        return response.data;
      } catch (error) {
        console.error('Error fetching AI models:', error);
        
        // If the endpoint doesn't exist (404), provide fallback data for development
        if (error.response?.status === 404) {
          console.warn('AI models endpoint not found, using fallback data for development');
          
          return {
            defaultModel: 'nova-micro',
            supportedModels: [
              'nova-micro', 'nova-lite', 'nova-pro',
              'claude-haiku', 'claude-sonnet', 'claude-opus',
              'deepseek-r1', 'deepseek-coder', 'deepseek-math',
              'gpt-4o-mini', 'gpt-4o'
            ],
            modelDetails: {
              // Amazon Models
              'nova-micro': {
                displayName: 'Nova Micro',
                description: 'Ultra-fast responses for simple tasks and quick suggestions',
                provider: 'Amazon'
              },
              'nova-lite': {
                displayName: 'Nova Lite',
                description: 'Balanced speed and quality for everyday coding tasks',
                provider: 'Amazon'
              },
              'nova-pro': {
                displayName: 'Nova Pro',
                description: 'Advanced reasoning for complex code analysis and generation',
                provider: 'Amazon'
              },
              
              // Anthropic Models
              'claude-haiku': {
                displayName: 'Claude 3 Haiku',
                description: 'Fast and efficient for quick code snippets and simple tasks',
                provider: 'Anthropic'
              },
              'claude-sonnet': {
                displayName: 'Claude 3.5 Sonnet',
                description: 'Excellent balance of speed, intelligence, and cost-effectiveness',
                provider: 'Anthropic'
              },
              'claude-opus': {
                displayName: 'Claude 3 Opus',
                description: 'Most capable model for complex reasoning and analysis',
                provider: 'Anthropic'
              },
              
              // DeepSeek Models
              'deepseek-r1': {
                displayName: 'DeepSeek R1',
                description: 'Advanced reasoning model with strong problem-solving capabilities',
                provider: 'DeepSeek'
              },
              'deepseek-coder': {
                displayName: 'DeepSeek Coder',
                description: 'Specialized for code generation, debugging, and optimization',
                provider: 'DeepSeek'
              },
              'deepseek-math': {
                displayName: 'DeepSeek Math',
                description: 'Optimized for mathematical reasoning and algorithmic problems',
                provider: 'DeepSeek'
              },
              
              // OpenAI Models
              'gpt-4o-mini': {
                displayName: 'GPT-4o Mini',
                description: 'Cost-effective model with good performance for most tasks',
                provider: 'OpenAI'
              },
              'gpt-4o': {
                displayName: 'GPT-4o',
                description: 'Latest multimodal model with advanced reasoning capabilities',
                provider: 'OpenAI'
              }
            }
          };
        }
        
        throw error;
      }
    }
  },

  // Folder API calls
  folders: {
    getAll: async (userId) => {
      if (!userId) {
        console.warn('getAll called without userId');
        return [];
      }
      try {
        const response = await axiosInstance.get('/api/folders', {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching all folders for ${userId}:`, error);
        return [];
      }
    },

    getTree: async (userId) => {
      if (!userId) {
        console.warn('getTree called without userId');
        return [];
      }
      try {
        const response = await axiosInstance.get('/api/folders/tree', {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching folder tree for ${userId}:`, error);
        return [];
      }
    },

    getById: async (folderId, userId) => {
      if (!folderId || !userId) {
        console.warn('getById called without required parameters');
        return null;
      }
      try {
        const response = await axiosInstance.get(`/api/folders/${folderId}`, {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching folder ${folderId}:`, error);
        return null;
      }
    },

    create: async (folderData, userId) => {
      if (!userId) {
        console.warn('create called without userId');
        return null;
      }
      try {
        const response = await axiosInstance.post('/api/folders', folderData, {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
      }
    },

    update: async (folderId, folderData, userId) => {
      if (!folderId || !userId) {
        console.warn('update called without required parameters');
        return null;
      }
      try {
        const response = await axiosInstance.put(`/api/folders/${folderId}`, folderData, {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error updating folder ${folderId}:`, error);
        throw error;
      }
    },

    delete: async (folderId, userId) => {
      if (!folderId || !userId) {
        console.warn('delete called without required parameters');
        return false;
      }
      try {
        await axiosInstance.delete(`/api/folders/${folderId}`, {
          headers: {
            'X-User-ID': userId
          }
        });
        return true;
      } catch (error) {
        console.error(`Error deleting folder ${folderId}:`, error);
        return false;
      }
    }
  },
  suggestions: {
    generate: async (content, model = null) => {
      if (!content) {
        console.error('generate called without content');
        return Promise.reject(new Error('Content is required'));
      }
      try {
        const url = model ? `/api/suggestions?model=${model}` : '/api/suggestions';
        const response = await axiosInstance.post(url, { content });
        return response.data;
      } catch (error) {
        console.error('Error generating AI suggestions:', error);
        throw error;
      }
    },
    
    // New summary endpoint for AIAssistant
    generateSummary: async (content, model = null) => {
      if (!content) {
        console.error('generateSummary called without content');
        return Promise.reject(new Error('Content is required'));
      }
      try {
        const url = model ? `/api/suggestions?model=${model}` : '/api/suggestions';
        const response = await axiosInstance.post(url, { content });
        return response.data;
      } catch (error) {
        console.error('Error generating AI summary:', error);
        
        // If the endpoint doesn't exist (404), provide mock data for development
        if (error.response?.status === 404) {
          console.warn('AI suggestions endpoint not found, using mock data for development');
          
          // Generate mock suggestions based on content
          const words = content.toLowerCase().split(/\s+/).filter(word => word.length > 3);
          const uniqueWords = [...new Set(words)].slice(0, 5);
          
          return {
            titles: [
              `${content.split(' ').slice(0, 4).join(' ')}...`,
              `Code Snippet: ${uniqueWords[0] || 'untitled'}`,
              `${new Date().toLocaleDateString()} Notes`
            ],
            summary: `A code snippet containing ${words.length} words with focus on ${uniqueWords.slice(0, 2).join(' and ')}. Created on ${new Date().toLocaleDateString()}.`,
            tags: [...uniqueWords.slice(0, 3), 'code', 'snippet']
          };
        }
        
        throw error;
      }
    }
  },

  // AI Analysis endpoint for meetings and other content
  ai: {
    analyze: async (content, promptType = 'ANALYZE_ALL') => {
      if (!content) {
        console.error('AI analyze called without content');
        return Promise.reject(new Error('Content is required'));
      }
      
      try {
        const requestBody = {
          content,
          promptType
        };
        
        const response = await axiosInstance.post('/api/ai/suggestions', requestBody);
        return response.data;
      } catch (error) {
        console.error('AI Analysis Error:', error.message);
        
        // Provide mock data for development if endpoint doesn't exist
        if (error.response?.status === 404) {
          console.warn('AI analysis endpoint not found, using mock data for development');
          
          const mockResponses = {
            'ACTION_ITEMS': {
              titles: ['Action Items'],
              summary: '• Complete project documentation by Friday\n• Schedule follow-up with stakeholders\n• Review budget allocation for Q4\n• Update project timeline',
              tags: ['action-items', 'tasks', 'deadlines'],
              confidence: 0.90
            },
            'KEY_DECISIONS': {
              titles: ['Key Decisions'],
              summary: '• Approved budget increase for development team\n• Decided to extend project deadline by 2 weeks\n• Agreed to implement new testing procedures\n• Chose vendor for cloud infrastructure',
              tags: ['decisions', 'budget', 'timeline', 'vendor'],
              confidence: 0.88
            },
            'FOLLOW_UP_TASKS': {
              titles: ['Follow-up Tasks'],
              summary: '• Send notes to all participants\n• Create project status report\n• Schedule one-on-one sessions with team leads\n• Prepare presentation for next board review',
              tags: ['follow-up', 'tasks', 'reports'],
              confidence: 0.87
            },
            'ANALYZE_ALL': {
              titles: ['Complete Analysis'],
              summary: 'SUMMARY:\nThis content covered key project updates and team coordination.\n\nACTION ITEMS:\n• Complete documentation by Friday\n• Schedule stakeholder review\n\nKEY DECISIONS:\n• Approved budget increase\n• Extended project deadline\n\nFOLLOW-UP TASKS:\n• Send notes\n• Create status report',
              tags: ['comprehensive', 'analysis', 'summary'],
              confidence: 0.89
            }
          };
          
          const mockResponse = mockResponses[promptType] || mockResponses['ANALYZE_ALL'];
          return mockResponse;
        }
        
        throw error;
      }
    }
  },

  // Document related API calls
  documents: {
    create: async (documentData) => {
      try {
        const response = await axiosInstance.post('/api/documents', documentData);
        return response.data;
      } catch (error) {
        console.error('Error creating document:', error);
        if (error.response) {
          console.error('Error response status:', error.response.status);
          console.error('Error response data:', error.response.data);
        }
        throw error;
      }
    },

    getById: async (documentId) => {
      if (!documentId) {
        console.error('getById called without document ID');
        return Promise.reject(new Error('Document ID is required'));
      }
      try {
        const response = await axiosInstance.get(`/api/documents/${documentId}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching document ${documentId}:`, error);
        
        if (error.response?.status === 403) {
          const enhancedError = new Error('Access denied: This document is private and you do not have permission to view it');
          enhancedError.code = 'DOCUMENT_ACCESS_DENIED';
          enhancedError.status = 403;
          enhancedError.documentId = documentId;
          throw enhancedError;
        } else if (error.response?.status === 404) {
          const enhancedError = new Error('Document not found');
          enhancedError.code = 'DOCUMENT_NOT_FOUND';
          enhancedError.status = 404;
          enhancedError.documentId = documentId;
          throw enhancedError;
        }
        
        throw error;
      }
    },

    getUserDocuments: async (userId, folderId = null, limit = 20) => {
      if (!userId) {
        console.warn('getUserDocuments called without userId');
        return [];
      }
      try {
        const params = new URLSearchParams();
        if (folderId) params.append('folderId', folderId);
        params.append('limit', limit.toString());
        
        const response = await axiosInstance.get(`/api/documents?${params}`, {
          headers: {
            'X-User-ID': userId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching user documents for ${userId}:`, error);
        return [];
      }
    },

    update: async (documentId, documentData) => {
      if (!documentId) {
        console.error('update called without document ID');
        return Promise.reject(new Error('Document ID is required'));
      }
      try {
        const response = await axiosInstance.put(`/api/documents/${documentId}`, documentData);
        return response.data;
      } catch (error) {
        console.error(`Error updating document ${documentId}:`, error);
        throw error;
      }
    },

    delete: async (documentId) => {
      if (!documentId) {
        console.error('delete called without document ID');
        return Promise.reject(new Error('Document ID is required'));
      }
      try {
        await axiosInstance.delete(`/api/documents/${documentId}`);
        return true;
      } catch (error) {
        console.error(`Error deleting document ${documentId}:`, error);
        throw error;
      }
    },

    getRecentPublic: async (limit = 10) => {
      try {
        const response = await axiosInstance.get(`/api/documents/public/recent?limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching recent public documents:', error);
        return [];
      }
    },

    generateAISuggestions: async (documentId) => {
      if (!documentId) {
        console.error('generateAISuggestions called without document ID');
        return Promise.reject(new Error('Document ID is required'));
      }
      try {
        const response = await axiosInstance.post(`/api/documents/${documentId}/ai-suggestions`);
        return response.data;
      } catch (error) {
        console.error(`Error generating AI suggestions for document ${documentId}:`, error);
        throw error;
      }
    }
  },

  // Generic HTTP methods for direct API calls
  get: async (url, config = {}) => {
    try {
      const response = await axiosInstance.get(url, config);
      return response;
    } catch (error) {
      console.error(`GET ${url} failed:`, error);
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.post(url, data, config);
      return response;
    } catch (error) {
      console.error(`POST ${url} failed:`, error);
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.put(url, data, config);
      return response;
    } catch (error) {
      console.error(`PUT ${url} failed:`, error);
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await axiosInstance.delete(url, config);
      return response;
    } catch (error) {
      console.error(`DELETE ${url} failed:`, error);
      throw error;
    }
  }
};

export default api;
